import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { FaEye, FaHeart, FaShoppingCart } from 'react-icons/fa';
import {
  getPublicProducts,
  getPublicCategories,
} from '../redux/user/landingpageproducts/landingpage_service_syn';
import LoaderScreen from './loader/loaderScreen';
import { UserGetCart } from '../redux/user/cart/cartServicesSync';
import { InfinitySpin } from 'react-loader-spinner';
import { updateCartIndicator } from '../redux/user/cart/cartSlices';
import { UserMajorCartCreation } from '../redux/user/cart/cartServicesSync';
import EmptyState from './EmptyState';
import { updateWishListIndicator } from '../redux/user/wish_list/wish_list_slices';
import {
  UserGetWishList,
  UserMajorWishListCreation,
} from '../redux/user/wish_list/wish_list_services_sync';

export default function FurnitureShowcase({ product }) {
  const dispatch = useDispatch();
  const [activeCategory, setActiveCategory] = useState('');
  const [hoveredItem, setHoveredItem] = useState(null);
  const navigate = useNavigate();

  const { profile } = useSelector((state) => state.user_profile);

  const { products, categories, loading, cartIndicator, wish_list } =
    useSelector((state) => ({
      ...state.landpage,
      cartIndicator: state.user_cart.cartIndicator,
      wishListIndicator: state.user_wish_list.wishListIndicator,
      wish_list: state.user_wish_list.wish_list,
    }));

  useEffect(() => {
    dispatch(getPublicCategories({ page: 0, limit: 100 }));

    if (profile) {
      dispatch(UserGetCart());
      dispatch(UserGetWishList());
    }

    dispatch(
      getPublicProducts({
        name: '""',
        category: '""',
        brand: '""',
        from: 0,
        to: 0,
        limit: 10,
      })
    );
  }, []);

  const handleMouseEnter = (itemId) => {
    setHoveredItem(itemId);
  };

  const handleMouseLeave = () => {
    setHoveredItem(null);
  };

  const handleCategoryProduct = (category) => {
    setActiveCategory(category);
    dispatch(
      getPublicProducts({
        name: '""',
        category: category === 'All' ? '""' : category,
        from: 0,
        to: 0,
        limit: 10,
      })
    );
  };

  const handleAddToCart = (item) => {
    dispatch(updateCartIndicator(item._id));

    dispatch(
      UserMajorCartCreation({
        productId: item._id,
        quantity: 1,
      })
    )
      .then(() => {
        dispatch(UserGetCart());
        dispatch(updateCartIndicator(''));
      })
      .catch(() => {
        dispatch(updateCartIndicator(''));
      });
  };

  const handleViewProduct = (itemId) => {
    navigate(`/product/${itemId}`);
  };

  const handleAddToWishlist = (item) => {
    dispatch(updateWishListIndicator(item._id));

    dispatch(
      UserMajorWishListCreation({
        productId: item._id,
        quantity: 1,
        price: item.price,
      })
    )
      .then(() => {
        dispatch(UserGetWishList());
        dispatch(updateWishListIndicator(''));
      })
      .catch(() => {
        dispatch(updateWishListIndicator(''));
      });
  };

  const isInWishlist = (itemId) => {
    return wish_list?.products?.some((item) => item.product === itemId);
  };

  return (
    <section className="product__section section--padding pt-0">
      <div className="container-fluid">
        <div className="mx-auto px-4 py-12">
          <h1 className="text-3xl font-bold text-center mb-8">
            Most Popular Items
          </h1>

          {/* Categories */}
          <div className="flex flex-wrap justify-center gap-4 mb-12">
            <button
              onClick={() => handleCategoryProduct('All')}
              className={`px-6 py-2 rounded-md transition-colors ${
                activeCategory === 'All' || activeCategory === ''
                  ? 'bg-red-500 text-white'
                  : 'border border-gray-300 hover:border-red-500'
              }`}
            >
              All
            </button>

            {categories?.docs?.map((category) => (
              <button
                key={category._id}
                onClick={() => handleCategoryProduct(category.name)}
                className={`px-6 py-2 rounded-md transition-colors ${
                  activeCategory === category.name
                    ? 'bg-red-500 text-white'
                    : 'border border-gray-300 hover:border-red-500'
                }`}
              >
                {category.name}
              </button>
            ))}
          </div>

          {/* Items Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8">
            {loading ? (
              <div className="col-span-full flex items-center justify-center min-h-[50vh]">
                <LoaderScreen />
              </div>
            ) : products?.docs?.length > 0 ? (
              products?.docs?.map((item) => (
                <div
                  key={item._id}
                  className="group relative bg-white rounded-lg shadow-md overflow-hidden flex flex-col h-full"
                  onMouseEnter={() => handleMouseEnter(item._id)}
                  onMouseLeave={handleMouseLeave}
                >
                  {typeof item.tags === 'string' &&
                    item.tags.toLowerCase().includes('new') && (
                      <div className="absolute top-4 left-4 bg-red-500 text-white text-sm px-2 py-1 rounded-md z-10">
                        New
                      </div>
                    )}

                  <div className="aspect-square overflow-hidden flex justify-center items-center bg-gray-100">
                    <img
                      src={
                        item.gallery?.[hoveredItem === item._id ? 1 : 0]
                          ?.pic_url
                      }
                      alt={item.product_name}
                      width={300}
                      height={300}
                      className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-300 cursor-pointer"
                      onClick={() => handleViewProduct(item._id)}
                    />
                  </div>

                  {/* Quick actions */}
                  <div className="absolute right-4 top-4 flex flex-col gap-2">
                    <button
                      className="p-1 bg-white rounded-full shadow-md hover:bg-gray-100"
                      onClick={() => handleViewProduct(item._id)}
                    >
                      <FaEye className="w-5 h-5" />
                    </button>
                    <button
                      className="p-1 bg-white rounded-full shadow-md hover:bg-gray-100"
                      onClick={() => handleAddToWishlist(item)}
                    >
                      <FaHeart
                        className={`w-5 h-5 ${
                          isInWishlist(item._id)
                            ? 'text-red-500'
                            : 'text-gray-400'
                        }`}
                      />
                    </button>
                  </div>

                  {/* Content */}
                  <div className="p-4 flex-grow">
                    <h3
                      className="font-semibold text-[2rem] leading-8 mb-2 min-h-[4rem] cursor-pointer"
                      onClick={() => handleViewProduct(item._id)}
                    >
                      {item.product_name}
                    </h3>
                    <div className="flex items-center gap-2">
                      <span className="text-red-500 text-xl font-bold">
                        {item.symbol}
                        {item.price}
                      </span>
                      {item.discount && (
                        <span className="text-gray-400 line-through text-lg">
                          {item.symbol}
                          {item.discount}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Add to cart */}
                  <div>
                    {item._id === cartIndicator ? (
                      <button className="w-full bg-red-500 text-white py-[0.35rem] flex items-center justify-center gap-2 mt-auto">
                        <InfinitySpin width="80" color="#FFFFFF" />
                      </button>
                    ) : (
                      <button
                        onClick={() => handleAddToCart(item)}
                        className="w-full bg-red-500 text-white py-[0.35rem] flex items-center justify-center gap-2 mt-auto"
                      >
                        <FaShoppingCart className="w-5 h-5" />
                        Add to cart
                      </button>
                    )}
                  </div>
                </div>
              ))
            ) : (
              <EmptyState
                message={`No products found in ${
                  activeCategory || 'this category'
                }`}
              />
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
